import React, { memo, useMemo } from "react";
import Header from "../Components/Header";
import { motion } from "framer-motion";
import { Target, Eye, Heart } from "lucide-react";
import team1 from "../Assets/logoImage.webp";
import team2 from "../Assets/logoImage.webp";
import team3 from "../Assets/logoImage.webp";
import FooterCta from "../Components/FooterCta";
import Footer from "../Components/Footer";

const AboutUsPage = () => {
  // ✅ Memoized mission/vision data
  const missionVision = useMemo(
    () => [
      {
        title: "Our Mission",
        icon: Target,
        text: "To digitally transform the Indian transport industry by providing affordable, easy-to-use software solutions that help transporters work smarter, grow faster, and compete globally.",
      },
      {
        title: "Our Vision",
        icon: Eye,
        text: "To become India's most trusted TMS platform, serving over 100,000 transport businesses and contributing to a more efficient, transparent, and sustainable logistics ecosystem.",
      },
    ],
    []
  );

  // ✅ Memoized team data
  const teamMembers = useMemo(
    () => [
      { name: "Amit Sharma", role: "CEO & Founder", image: team1 },
      { name: "Priya Desai", role: "Chief Product Officer", image: team2 },
      { name: "Rahul Verma", role: "Head of Engineering", image: team3 },
    ],
    []
  );

  return (
    <main className="bg-[var(--page-bg)] text-[var(--black)]">
      <Header />

      {/* ===== Hero Section ===== */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-[#fdfdfd] to-[#f6f8fa] text-center">
        <div className="max-w-[1200px] mx-auto px-4 lg:px-8">
          <motion.h1
            className="font-heading text-[28px] lg:text-[48px] font-bold mb-5 leading-tight"
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, type: "spring" }}
          >
            All Transport Operations. One Smart System.
          </motion.h1>

          <motion.p
            className="text-[16px] lg:text-[18px] text-[var(--gray-text)] max-w-[100%] lg:max-w-[75%] mx-auto leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            MaalWahak offers a powerful suite of features designed for Indian
            transport and logistics businesses — helping you manage everything
            from bookings to billing effortlessly.
          </motion.p>
        </div>
      </section>

      {/* ===== Why We Built Section ===== */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="font-heading text-3xl font-bold mb-6 lg:text-4xl">
            Why we built MaalWahak
          </h2>
          <p className="text-muted-foreground mb-6">
            The Indian transport industry is the backbone of our economy, yet it
            still relies heavily on manual processes and outdated systems. We
            saw transporters struggling with paperwork, inefficient
            communication, and lack of real-time visibility into their
            operations.
          </p>
          <p className="text-muted-foreground">
            MaalWahak was born from a simple vision: to bring the power of
            modern technology to every transport business in India, regardless
            of their size.
          </p>
        </div>
      </section>

      {/* ===== Mission & Vision ===== */}
      <section className="py-20 bg-[#f9fafb]">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 grid gap-12 md:grid-cols-2">
          {missionVision.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                className="text-center p-8 bg-white rounded-2xl shadow-sm hover:shadow-md transition-all"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-lg bg-primary/10 mb-6">
                  <Icon className="h-8 w-8 text-[var(--primary-color)]" />
                </div>
                <h3 className="font-heading text-2xl font-bold mb-4">
                  {item.title}
                </h3>
                <p className="text-muted-foreground">{item.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ===== Team Section ===== */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-4">
            Meet Our Team
          </h2>
          <p className="text-muted-foreground text-lg mb-12">
            Passionate professionals dedicated to transforming the transport
            industry
          </p>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                className="rounded-xl border bg-card p-6 text-center shadow-sm hover:shadow-lg transition-all"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <img
                  src={member.image}
                  alt={member.name}
                  loading="lazy"
                  className="h-24 w-24 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="font-heading text-lg font-semibold mb-1">
                  {member.name}
                </h3>
                <p className="text-sm text-muted-foreground">{member.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <FooterCta
        title={"Ready to transform your transport business?"}
        subtitle="Schedule a demo and see how MaalWahak can help you grow"
      />
      <Footer />
    </main>
  );
};

export default memo(AboutUsPage);
